// Shared SEO primitives. Kept free of Nuxt composables so they can be unit tested and
// reused by both `useSetHead` (title/canonical) and the JSON-LD builders in `~/utils/schemaOrg`.

/**
 * Robots rule for a resolved page, or `null` when the page keeps the site default.
 *
 * Shopware sends `robots` in the url-route `seo` block (e.g. `noindex,follow` for categories with
 * the noindex checkbox on the SEO tab). The value is normalised to the `a, b` spacing nuxt-robots
 * uses so the meta tag and the X-Robots-Tag header look the same everywhere.
 */
export function robotsRuleFromSeo(seo?: { robots?: string | null } | null): string | null {
    const directives = (seo?.robots ?? '')
        .split(',')
        .map(directive => directive.trim().toLowerCase())
        .filter(Boolean);
    return directives.length ? directives.join(', ') : null;
}

/** Query params that narrow, reorder or paginate a product listing. */
export const LISTING_QUERY_PARAMS = [
    'properties',
    'min-price',
    'max-price',
    'tn_cid',
    'tn_q',
    'search',
    'newOnly',
    'sort',
] as const;

export function trimTrailingSlash(url: string): string {
    return url.replace(/\/+$/, '');
}

/**
 * Absolute URL for a site-relative path. Paths that are already absolute are returned
 * unchanged, so breadcrumb URLs coming from Shopware work either way.
 */
export function absoluteUrl(baseUrl: string, path: string): string {
    if (!path) return trimTrailingSlash(baseUrl);
    if (/^https?:\/\//i.test(path)) return path;
    return trimTrailingSlash(baseUrl) + (path.startsWith('/') ? path : '/' + path);
}

/**
 * The page title as rendered in `<title>`: the Shopware meta title plus the site suffix,
 * with a duplicated "| Ballegooyen Modes" collapsed into one.
 */
export function buildPageMetaTitle(metaTitle?: string | null, suffix?: string | null): string {
    const title = (metaTitle ?? '') + ' ' + (suffix ?? '') || '| Ballegooyen Modes';
    return title.replace(/(\| Ballegooyen Modes)\s?\1/g, '$1').trim();
}

/**
 * Product detail path for a listing product. Mirrors the link built in
 * `ShopwareProduct` so the JSON-LD URLs match the rendered anchors exactly.
 */
export function productPathFromSeoUrls(product: any): string | null {
    const seoPathInfo = product?.seoUrls?.[0]?.seoPathInfo;
    if (!seoPathInfo) return null;
    return (
        (
            '/nl/' +
            String(seoPathInfo)
                .toLowerCase()
                .replace(/^(\/?(nl\/)+)+/, '')
        ).split('?')[0] ?? null
    );
}

/** Plain text from an HTML fragment (SEO top content / category descriptions). */
export function stripHtml(html?: string | null): string {
    if (!html) return '';
    return (
        html
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/\s+/g, ' ')
            // Tags become spaces, so inline markup ("<strong>normaal</strong>.") would otherwise
            // leave a gap before the punctuation that follows it.
            .replace(/\s+([.,;:!?)])/g, '$1')
            .trim()
    );
}

const HEADING_PATTERN = /<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi;

/**
 * Question/answer pairs from an HTML content block. The brand and category SEO texts write
 * their FAQ as a heading ending in a question mark followed by the answer, so that is exactly
 * what is matched — anything else in the same block is left alone.
 */
export function extractFaqFromHtml(html?: string | null): Array<{ question: string; answer: string }> {
    if (!html) return [];

    const headings = [...html.matchAll(HEADING_PATTERN)];

    return headings
        .map((heading, index) => {
            const question = stripHtml(heading[2]);
            if (!question.endsWith('?')) return undefined;

            const start = (heading.index ?? 0) + heading[0].length;
            const end = headings[index + 1]?.index ?? html.length;
            const answer = stripHtml(html.slice(start, end));

            return answer ? { question, answer } : undefined;
        })
        .filter(Boolean) as Array<{ question: string; answer: string }>;
}

/** Sanity's stega encoding hides edit metadata in invisible characters — never emit those. */
const INVISIBLE_CHARS = /[\u200B-\u200F\uFEFF]|[\u{E0000}-\u{E007F}]/gu;

const PORTABLE_TEXT_HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

function portableTextBlockToPlainText(block: any): string {
    if (block?._type !== 'block' || !Array.isArray(block?.children)) return '';
    return block.children
        .map((child: any) => child?.text ?? '')
        .join('')
        .replace(INVISIBLE_CHARS, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Plain text from a Sanity Portable Text array (or a legacy plain string). */
export function portableTextToPlainText(blocks: unknown): string {
    if (typeof blocks === 'string') return blocks.replace(INVISIBLE_CHARS, '').trim();
    if (!Array.isArray(blocks)) return '';

    return blocks.map(portableTextBlockToPlainText).filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Question/answer pairs from Portable Text, using the same rule as the HTML variant: a heading
 * that ends in a question mark, answered by the blocks that follow it up to the next heading.
 */
export function extractFaqFromPortableText(blocks: unknown): Array<{ question: string; answer: string }> {
    if (!Array.isArray(blocks)) return [];

    const entries: Array<{ question: string; answer: string }> = [];
    let question: string | undefined;
    let answer: string[] = [];

    const flush = () => {
        if (question && answer.length) entries.push({ question, answer: answer.join(' ') });
        question = undefined;
        answer = [];
    };

    for (const block of blocks) {
        if ((block as any)?._type !== 'block') continue;
        const text = portableTextBlockToPlainText(block);
        if (!text) continue;

        if (PORTABLE_TEXT_HEADINGS.has((block as any).style)) {
            flush();
            question = text.endsWith('?') ? text : undefined;
            continue;
        }
        if (question) answer.push(text);
    }
    flush();

    return entries;
}

/** Truncate on a word boundary — used for meta-description fallbacks. */
export function truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    const cut = text.slice(0, maxLength);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim() + '…';
}

/**
 * Phone number in E.164 notation for schema.org. Dutch national numbers ("0416 391230")
 * are prefixed with the country code; numbers that already carry one are kept.
 */
export function toE164(phone?: string | null, countryCode = '+31'): string {
    if (!phone) return '';
    const cleaned = phone.replace(/[^\d+]/g, '');
    if (!cleaned) return '';
    if (cleaned.startsWith('+')) return cleaned;
    if (cleaned.startsWith('00')) return '+' + cleaned.slice(2);
    if (cleaned.startsWith('0')) return countryCode + cleaned.slice(1);
    return countryCode + cleaned;
}

/**
 * True when the URL narrows, reorders or paginates the listing. The canonical URL drops the
 * query string, so in those cases the visible products no longer describe the canonical page.
 */
export function hasListingQueryParams(query: Record<string, any>): boolean {
    if (LISTING_QUERY_PARAMS.some(param => !!query?.[param])) return true;
    const page = Array.isArray(query?.p) ? query.p[0] : query?.p;
    return !!page && String(page) !== '1';
}
